import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";

const dir = (v) => (v === "asc" ? 1 : -1);

export const DataTable = ({
  columns,
  rows,
  testId = "data-table",
  onRowClick,
  maxHeight = "",
  minWidth = "min-w-[60rem]",
  defaultSort = null,
}) => {
  const [sort, setSort] = useState(defaultSort); // { key, order }

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = (r) => (col.sortValue ? col.sortValue(r) : r[col.key]);
    return [...rows].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const xn = x === null || x === undefined || x === "";
      const yn = y === null || y === undefined || y === "";
      if (xn && yn) return 0;
      if (xn) return 1; // blanks always sink
      if (yn) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir(sort.order);
      return String(x).localeCompare(String(y)) * dir(sort.order);
    });
  }, [rows, sort, columns]);

  const toggle = (c) => {
    if (c.sortable === false) return;
    setSort((s) =>
      !s || s.key !== c.key
        ? { key: c.key, order: c.align === "right" ? "desc" : "asc" }
        : s.order === "asc"
          ? { key: c.key, order: "desc" }
          : null
    );
  };

  return (
    <div
      className={`overflow-auto border border-stone-200 rounded-xl bg-white ${maxHeight}`}
      data-testid={testId}
    >
      <table className={`w-full ${minWidth} text-[14px] border-collapse`}>
        <thead className="sticky top-0 z-10">
          <tr className="bg-stone-50/95 backdrop-blur-sm border-b border-stone-200">
            {columns.map((c) => {
              const on = sort?.key === c.key;
              const can = c.sortable !== false;
              return (
                <th
                  key={c.key}
                  onClick={() => toggle(c)}
                  title={c.headerTitle}
                  data-testid={`${testId}-sort-${c.key}`}
                  className={`px-4 py-2.5 font-semibold text-[12px] whitespace-nowrap select-none ${
                    can ? "cursor-pointer group hover:text-stone-900" : ""
                  } ${on ? "text-stone-900" : "text-stone-600"} ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  <span
                    className={`inline-flex items-center gap-1 ${
                      c.align === "right" ? "flex-row-reverse" : ""
                    }`}
                  >
                    {c.label}
                    {can && c.label ? (
                      on ? (
                        sort.order === "asc" ? (
                          <ArrowUp className="w-3 h-3 text-[#A01236]" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-[#A01236]" />
                        )
                      ) : (
                        <ChevronsUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />
                      )
                    ) : null}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-stone-600">
                No rows matched this slice.
              </td>
            </tr>
          )}
          {sorted.map((r, i) => (
            <tr
              key={i}
              onClick={() => onRowClick?.(r)}
              data-testid={`${testId}-row-${i}`}
              className={`border-b border-stone-100 last:border-0 transition-colors ${
                onRowClick ? "cursor-pointer hover:bg-[#FBEEF2]/50" : "hover:bg-stone-50/70"
              }`}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-3 align-middle ${
                    c.align === "right"
                      ? "text-right num text-stone-800"
                      : "text-stone-700"
                  } ${c.className || ""}`}
                >
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Horizontal meter for a 0-100 value, shown beside the figure. */
export const Meter = ({ value, tone = "brand", width = "w-14" }) => {
  if (value === null || value === undefined) return <span className="text-stone-500">—</span>;
  const v = Math.max(0, Math.min(100, Number(value)));
  const color =
    tone === "warn"
      ? v > 20
        ? "bg-rose-500"
        : v > 10
          ? "bg-amber-500"
          : "bg-emerald-500"
      : v >= 85
        ? "bg-emerald-500"
        : v >= 60
          ? "bg-amber-500"
          : "bg-rose-500";
  return (
    <span className="inline-flex items-center gap-2 justify-end">
      <span className="tabular-nums">{v.toFixed(1)}%</span>
      <span className={`${width} h-1.5 bg-stone-100 rounded-full overflow-hidden shrink-0`}>
        <span className={`block h-full rounded-full ${color}`} style={{ width: `${v}%` }} />
      </span>
    </span>
  );
};

const CHIP_TONES = {
  consistent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  variable: "bg-amber-50 text-amber-700 border-amber-200",
  erratic: "bg-rose-50 text-rose-700 border-rose-200",
  reallocate: "bg-rose-50 text-rose-700 border-rose-200",
  review: "bg-amber-50 text-amber-700 border-amber-200",
  "verify reporting": "bg-sky-50 text-sky-700 border-sky-200",
  muted: "bg-stone-50 text-stone-600 border-stone-200",
};

export const Chip = ({ label, tone = "muted", title }) => (
  <span
    title={title}
    className={`inline-block text-[12px] font-medium px-2 py-0.5 rounded-full border ${
      CHIP_TONES[tone] || CHIP_TONES.muted
    }`}
  >
    {label}
  </span>
);
